import { createContext, useContext, useState, ReactNode } from "react";
import { products as staticProducts } from "@/data/products";
import type { Product } from "@/data/products";

interface AdminProductsValue {
  products: Product[];
  addProduct: (p: Omit<Product, "id">) => void;
  updateProduct: (id: string, p: Partial<Product>) => void;
  deleteProduct: (id: string) => void;
  isDemo: boolean;
}

const AdminProductsContext = createContext<AdminProductsValue | null>(null);

const supabaseConfigured = !!(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY
);

export function AdminProductsProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([...staticProducts]);

  function addProduct(data: Omit<Product, "id">) {
    const newProduct: Product = {
      ...data,
      id: `p-${Date.now()}`,
    };
    setProducts((prev) => [newProduct, ...prev]);
  }

  function updateProduct(id: string, data: Partial<Product>) {
    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, ...data } : p))
    );
  }

  function deleteProduct(id: string) {
    setProducts((prev) => prev.filter((p) => p.id !== id));
  }

  return (
    <AdminProductsContext.Provider
      value={{ products, addProduct, updateProduct, deleteProduct, isDemo: !supabaseConfigured }}
    >
      {children}
    </AdminProductsContext.Provider>
  );
}

export function useAdminProducts() {
  const ctx = useContext(AdminProductsContext);
  if (!ctx) throw new Error("useAdminProducts must be inside AdminProductsProvider");
  return ctx;
}
